import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { WORK_STATE_LABELS, type WorkState } from '@scc/shared';
import {
  createWorkItem,
  fetchPeople,
  fetchProjects,
  updateWorkItem,
  uploadAttachment,
  type Me,
  type WorkItem,
} from '../api';
import { ErrorBox } from '../components/ui';
import { AdjuntosAlAlta } from '../components/AdjuntosAlAlta';

const ESTADOS: WorkState[] = [
  'backlog',
  'ready',
  'in_development',
  'in_review',
  'in_testing',
  'ready_for_prod',
  'in_production',
  'blocked',
  'done',
  'cancelled',
];
const PRIORIDADES = ['P0', 'P1', 'P2', 'P3'];
const TIPOS: Array<{ value: WorkItem['kind']; label: string }> = [
  { value: 'task', label: 'Tarea' },
  { value: 'feature', label: 'Función' },
  { value: 'bug', label: 'Error' },
];

/**
 * Crear y editar tareas.
 *
 * La API para esto existía desde H6 y ninguna pantalla la usaba: se podía asignar trabajo
 * por HTTP y no desde el portal, que es como decir que no se podía. Esto lo cierra.
 *
 * Lo que se puede tocar depende del papel, y la pantalla lo REFLEJA en vez de dejar que la
 * API rechace la petición después: un colaborador ve solo los campos de avance de las
 * tareas que tiene asignadas. Enseñar un campo que el servidor va a rechazar es prometer
 * algo que no se cumple.
 *
 * Las tareas que vienen de un issue de GitHub no se editan aquí — se gestionan allí, que
 * es su fuente de verdad. También se dice, en vez de fallar al guardar.
 */
export function TaskEditor({
  me,
  item,
  projectSlug,
  onDone,
  onCancel,
}: {
  me: Me;
  /** Sin item = alta. */
  item?: WorkItem;
  /** Proyecto fijado cuando se crea desde la ficha de un proyecto. */
  projectSlug?: string;
  onDone: () => void;
  onCancel: () => void;
}): JSX.Element {
  const client = useQueryClient();
  const esAlta = item === undefined;
  const esAdmin = me.role === 'admin';
  const suya = (item?.assigneeLogin ?? '').toLowerCase() === me.login.toLowerCase();
  const deGithub = item?.origin === 'github';

  const [titulo, setTitulo] = useState(item?.title ?? '');
  const [descripcion, setDescripcion] = useState(item?.description ?? '');
  const [proyecto, setProyecto] = useState(item?.projectSlug ?? projectSlug ?? '');
  const [tipo, setTipo] = useState<WorkItem['kind']>(item?.kind ?? 'task');
  const [estado, setEstado] = useState(item?.state ?? 'backlog');
  const [prioridad, setPrioridad] = useState(item?.priority ?? 'P2');
  const [asignado, setAsignado] = useState(item?.assigneeLogin ?? '');
  const [fecha, setFecha] = useState(item?.targetDate?.slice(0, 10) ?? '');
  // Imágenes y vídeos elegidos antes de que la tarea exista. Viven aquí y no en el
  // componente que los enseña porque quien los sube es esta pantalla, y solo puede hacerlo
  // cuando la creación ha devuelto un id.
  const [adjuntos, setAdjuntos] = useState<File[]>([]);
  /**
   * Alta que salió a medias: la tarea SÍ se creó y alguna imagen no subió.
   *
   * Tiene estado propio porque cambia lo que se puede hacer: el formulario deja de
   * aceptar envíos. Si siguiera vivo, un segundo «Crear tarea» crearía una tarea
   * DUPLICADA — el usuario creería estar reintentando la imagen y estaría creando otra
   * tarea. Por eso a partir de aquí lo único que queda es cerrar.
   */
  const [aMedias, setAMedias] = useState<{ id: number; fallos: string[] } | null>(null);

  const proyectos = useQuery({ queryKey: ['projects'], queryFn: fetchProjects, enabled: esAlta });
  const personas = useQuery({ queryKey: ['people-access'], queryFn: fetchPeople });

  const guardar = useMutation({
    mutationFn: async () => {
      if (esAlta) {
        const creada = await createWorkItem({
          projectSlug: proyecto,
          kind: tipo,
          title: titulo.trim(),
          description: descripcion.trim() || undefined,
          state: estado,
          priority: prioridad,
          assigneeLogin: asignado || undefined,
          targetDate: fecha || undefined,
        });

        // Las imágenes van DESPUÉS y no pueden ir antes: el endpoint de subida cuelga de
        // `work-items/:id`, y ese id no existe hasta que la tarea está creada.
        //
        // Cada fallo se recoge y NO interrumpe las demás. Que la segunda imagen falle no
        // es razón para no subir la tercera, y sobre todo no es razón para presentar el
        // alta como fallida: la tarea ya está creada, y decir «error al crear la tarea»
        // sería falso — mandaría al usuario a crearla otra vez, duplicándola.
        const fallos: string[] = [];
        for (const adjunto of adjuntos) {
          try {
            await uploadAttachment(creada.id, adjunto);
          } catch (e) {
            fallos.push(`${adjunto.name}: ${e instanceof Error ? e.message : 'no se pudo subir'}`);
          }
        }
        return { creadaId: creada.id, fallos };
      }
      // Un colaborador manda solo lo suyo: si enviara los demás campos, el servidor
      // rechazaría la petición entera aunque no hubiera cambiado nada de ellos.
      const cambios = esAdmin
        ? {
            title: titulo.trim(),
            description: descripcion,
            kind: tipo,
            state: estado,
            priority: prioridad,
            assigneeLogin: asignado || null,
            targetDate: fecha || null,
          }
        : { state: estado, description: descripcion };
      await updateWorkItem(item.id, cambios);
      return { creadaId: null, fallos: [] as string[] };
    },
    onSuccess: (resultado) => {
      // Se refresca en los dos casos: la tarea existe, con imágenes o sin ellas, y la
      // lista tiene que enseñarla aunque esta pantalla se quede abierta explicando el
      // problema.
      void client.invalidateQueries();
      if (resultado.creadaId !== null && resultado.fallos.length > 0) {
        setAMedias({ id: resultado.creadaId, fallos: resultado.fallos });
        return; // No se cierra: hay algo que contar y el formulario ya no debe reenviarse.
      }
      onDone();
    },
  });

  if (deGithub && !esAlta) {
    return (
      <div className="space-y-3 text-[13px]">
        <p className="text-[var(--color-ink-muted)]">
          Esta tarea es el reflejo de un issue de GitHub. El título, el estado, la prioridad y quién
          la lleva se gestionan allí; cambiarlo aquí duraría hasta la siguiente sincronización.
        </p>
        {item.sourceRef && (
          <a href={item.sourceRef} target="_blank" rel="noreferrer" className="underline">
            Abrir en GitHub
          </a>
        )}
        <button type="button" onClick={onCancel} className="block text-[var(--color-ink-muted)]">
          cerrar
        </button>
      </div>
    );
  }

  if (!esAlta && !esAdmin && !suya) {
    return (
      <div className="space-y-3 text-[13px]">
        <p className="text-[var(--color-ink-muted)]">
          Esta tarea no está asignada a ti, así que solo puedes leerla. Pide a un administrador que
          te la asigne o que la cambie.
        </p>
        <button type="button" onClick={onCancel} className="text-[var(--color-ink-muted)]">
          cerrar
        </button>
      </div>
    );
  }

  // La tarea existe; lo que falló fueron imágenes. Esta pantalla ya no es un formulario:
  // deja de ofrecer «Crear tarea» para que nadie cree un duplicado creyendo que reintenta
  // la subida. Las que sí subieron se quedan puestas.
  if (aMedias) {
    return (
      <div className="space-y-3 text-[13px]">
        <p>
          <strong className="font-semibold">La tarea se creó</strong> (#{aMedias.id}), pero{' '}
          {aMedias.fallos.length === 1
            ? 'un adjunto no se pudo subir'
            : `${aMedias.fallos.length} adjuntos no se pudieron subir`}
          :
        </p>
        <ul className="space-y-1 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-[11px] leading-snug text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-200">
          {aMedias.fallos.map((fallo) => (
            <li key={fallo}>{fallo}</li>
          ))}
        </ul>
        <p className="text-[12px] text-[var(--color-ink-muted)]">
          Las demás sí están. Puedes añadir las que faltan abriendo la tarea: no hace falta
          crearla otra vez.
        </p>
        <button type="button" onClick={onDone} className="text-[var(--color-ink-muted)]">
          cerrar
        </button>
      </div>
    );
  }

  const campoCompleto = esAlta || esAdmin;
  const listo = titulo.trim().length > 0 && (esAlta ? proyecto.length > 0 : true);

  return (
    <form
      className="space-y-3 text-[13px]"
      onSubmit={(e) => {
        e.preventDefault();
        if (listo) guardar.mutate();
      }}
    >
      {campoCompleto && (
        <Campo etiqueta="Título">
          <input
            value={titulo}
            onChange={(e) => setTitulo(e.target.value)}
            maxLength={300}
            className="w-full rounded border border-[var(--color-border-subtle)] bg-[var(--color-surface)] px-2 py-1"
          />
        </Campo>
      )}

      {esAlta && (
        <Campo etiqueta="Proyecto">
          <select
            value={proyecto}
            onChange={(e) => setProyecto(e.target.value)}
            className="w-full rounded border border-[var(--color-border-subtle)] bg-[var(--color-surface)] px-2 py-1"
          >
            <option value="">Elige uno…</option>
            {(proyectos.data ?? []).map((p) => (
              <option key={p.slug} value={p.slug}>
                {p.name}
              </option>
            ))}
          </select>
        </Campo>
      )}

      <Campo etiqueta="Descripción">
        <textarea
          value={descripcion}
          onChange={(e) => setDescripcion(e.target.value)}
          rows={4}
          className="w-full rounded border border-[var(--color-border-subtle)] bg-[var(--color-surface)] px-2 py-1"
        />
      </Campo>

      <Campo etiqueta="Estado">
        <select
          value={estado}
          onChange={(e) => setEstado(e.target.value)}
          className="w-full rounded border border-[var(--color-border-subtle)] bg-[var(--color-surface)] px-2 py-1"
        >
          {ESTADOS.map((s) => (
            <option key={s} value={s}>
              {WORK_STATE_LABELS[s] ?? s}
            </option>
          ))}
        </select>
      </Campo>

      {campoCompleto && (
        <>
          <Campo etiqueta="Tipo">
            <select
              value={tipo}
              onChange={(e) => setTipo(e.target.value as WorkItem['kind'])}
              className="w-full rounded border border-[var(--color-border-subtle)] bg-[var(--color-surface)] px-2 py-1"
            >
              {TIPOS.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </Campo>

          <Campo etiqueta="Prioridad">
            <select
              value={prioridad}
              onChange={(e) => setPrioridad(e.target.value)}
              className="w-full rounded border border-[var(--color-border-subtle)] bg-[var(--color-surface)] px-2 py-1"
            >
              {PRIORIDADES.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </Campo>

          <Campo etiqueta="Asignada a">
            <select
              value={asignado}
              onChange={(e) => setAsignado(e.target.value)}
              className="w-full rounded border border-[var(--color-border-subtle)] bg-[var(--color-surface)] px-2 py-1"
            >
              <option value="">Sin asignar</option>
              {(personas.data?.people ?? [])
                .filter((p) => p.can_login && p.github_login)
                .map((p) => (
                  <option key={p.id} value={p.github_login ?? ''}>
                    {p.display_name} (@{p.github_login})
                  </option>
                ))}
            </select>
          </Campo>

          <Campo etiqueta="Fecha objetivo">
            <input
              type="date"
              value={fecha}
              onChange={(e) => setFecha(e.target.value)}
              className="w-full rounded border border-[var(--color-border-subtle)] bg-[var(--color-surface)] px-2 py-1"
            />
          </Campo>
        </>
      )}

      {!campoCompleto && (
        <p className="text-[12px] text-[var(--color-ink-muted)]">
          Como colaborador mueves el avance de tus tareas. Quién la lleva y qué prioridad tiene los
          cambia un administrador.
        </p>
      )}

      {esAlta && (
        <AdjuntosAlAlta
          adjuntos={adjuntos}
          onCambiar={setAdjuntos}
          limites={{
            imagen: me.attachmentsMaxMb,
            video: me.attachmentsVideoMaxMb,
            documento: me.attachmentsDocMaxMb,
          }}
        />
      )}

      <div className="flex items-center gap-3 pt-1">
        <button
          type="submit"
          disabled={!listo || guardar.isPending}
          className="rounded bg-[var(--color-ink)] px-3 py-1.5 text-[13px] text-[var(--color-surface)] disabled:opacity-40"
        >
          {guardar.isPending
            ? esAlta && adjuntos.length > 0
              ? 'Creando y subiendo…'
              : 'Guardando…'
            : esAlta
              ? 'Crear tarea'
              : 'Guardar'}
        </button>
        <button type="button" onClick={onCancel} className="text-[var(--color-ink-muted)]">
          cancelar
        </button>
      </div>
      {guardar.isError && <ErrorBox error={guardar.error} />}
    </form>
  );
}

function Campo({
  etiqueta,
  children,
}: {
  etiqueta: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <label className="block">
      <span className="mb-1 block text-[12px] text-[var(--color-ink-muted)]">{etiqueta}</span>
      {children}
    </label>
  );
}
